<template>
    <section class="timesheet-activity">
        <DiscussionTarget />
        <header class="timesheet-activity__header">
            <div>
                <h2>Комментарии и история</h2>
                <p>Изменения ячеек собраны по сеансам, служебные действия показаны отдельно.</p>
            </div>
            <b-button size="sm" variant="outline-secondary" :disabled="loading" @click="$emit('refresh')">
                <i class="fas fa-sync-alt" aria-hidden="true"></i>
                Обновить
            </b-button>
        </header>

        <div class="timesheet-activity__tabs" role="tablist" aria-label="Комментарии и история табеля">
            <button
                type="button"
                role="tab"
                :class="{active: activeSection === 'comments'}"
                :aria-selected="activeSection === 'comments' ? 'true' : 'false'"
                @click="activeSection = 'comments'"
            >
                <i class="fas fa-comment" aria-hidden="true"></i>
                Комментарии
                <span>{{ commentItems.length }}</span>
            </button>
            <button
                type="button"
                role="tab"
                :class="{active: activeSection === 'history'}"
                :aria-selected="activeSection === 'history' ? 'true' : 'false'"
                @click="activeSection = 'history'"
            >
                <i class="fas fa-history" aria-hidden="true"></i>
                История
                <span>{{ historyItems.length }}</span>
            </button>
        </div>

        <div v-if="activeSection === 'comments'" class="timesheet-activity__composer">
            <DiscussionEditor primary
                v-model="newComment"
                maxlength="10000"
                placeholder="Написать комментарий по табелю…"
            ></DiscussionEditor>
            <b-button
                variant="primary"
                :disabled="pending || !newComment"
                @click="createComment"
            >
                Отправить
            </b-button>
        </div>

        <div v-if="loading" class="timesheet-activity__empty">
            <b-spinner small></b-spinner>
            Загружаем историю…
        </div>
        <div v-else-if="!displayedItems.length" class="timesheet-activity__empty">
            {{ activeSection === 'comments' ? 'Комментариев пока нет.' : 'История изменений пока пуста.' }}
        </div>
        <ol v-else class="timesheet-activity__feed">
            <li v-for="item in displayedItems" :key="item.id" :class="`is-${item.kind}`">
                <div class="timesheet-activity__marker">
                    <i :class="item.kind === 'comment' ? 'fas fa-comment' : 'fas fa-history'" aria-hidden="true"></i>
                </div>
                <article>
                    <header>
                        <strong>{{ item.actor_name || 'Система' }}</strong>
                        <time>{{ formatDateTime(item.created_at) }}</time>
                    </header>

                    <template v-if="item.kind === 'comment'">
                        <p v-if="item.deleted_at" class="timesheet-activity__deleted">Комментарий удалён</p>
                        <template v-else-if="editingCommentId === item.comment_id">
                            <DiscussionEditor source-type="timesheet_comment" :source-id="item.comment_id" @saved="cancelEdit" v-model="editCommentText"></DiscussionEditor>
                            <div class="timesheet-activity__comment-actions">
                                <b-button size="sm" variant="primary" :disabled="pending || !editCommentText" @click="saveEdit(item)">
                                    Сохранить
                                </b-button>
                                <b-button size="sm" variant="link" :disabled="pending" @click="cancelEdit">Отмена</b-button>
                            </div>
                        </template>
                        <template v-else>
                            <DiscussionText :value="item.body || ''" source-type="timesheet_comment" :source-id="item.comment_id" />
                            <small v-if="item.edited_at">изменено {{ formatDateTime(item.edited_at) }}</small>
                            <div v-if="item.can_edit" class="timesheet-activity__comment-actions">
                                <button type="button" @click="beginEdit(item)">Изменить</button>
                                <button type="button" class="is-danger" @click="$emit('delete-comment', item)">Удалить</button>
                            </div>
                        </template>
                    </template>

                    <template v-else>
                        <p>{{ eventLabel(item) }}</p>
                        <DiscussionText v-if="item.payload && item.payload.reason" :value="item.payload.reason" source-type="timesheet_history" :source-id="String(item.id).replace('event:', '')" />
                        <router-link v-if="item.payload && item.payload.request_id && String(item.event_type).startsWith('submission_extension_')"
                            :to="`/requests/${item.payload.request_id}`">Открыть заявку на продление</router-link>
                        <small v-if="item.payload && item.payload.resubmission_due_date">Срок исправления: {{ item.payload.resubmission_due_date }}, 23:59:59 (Минск)</small>
                        <ul
                            v-if="['assigned_roster_synchronized', 'unassigned_empty_roster_lines_removed'].includes(item.event_type) && rosterEmployees(item).length"
                            class="timesheet-activity__roster-additions"
                        >
                            <li
                                v-for="employee in rosterEmployees(item)"
                                :key="`${item.id}-employee-${employee.employee_id}`"
                            >
                                <strong>{{ employee.employee_name }}</strong>
                                <span
                                    v-for="(assignment, index) in employee.assignments || []"
                                    :key="`${item.id}-employee-${employee.employee_id}-assignment-${index}`"
                                >
                                    {{ assignmentLabel(assignment) }}
                                </span>
                                <span v-if="item.event_type === 'unassigned_empty_roster_lines_removed'">
                                    Пустая строка: назначений на объект за месяц больше нет
                                </span>
                                <span v-else-if="!employee.assignments || !employee.assignments.length">
                                    Назначение на объект в периоде табеля
                                </span>
                            </li>
                        </ul>
                        <button
                            v-if="item.changes && item.changes.length"
                            type="button"
                            class="timesheet-activity__details-toggle"
                            @click="$set(openEvents, item.id, !openEvents[item.id])"
                        >
                            {{ openEvents[item.id] ? 'Скрыть изменения' : `Показать изменения (${item.changes.length})` }}
                        </button>
                        <ul v-if="openEvents[item.id]" class="timesheet-activity__changes">
                            <li v-for="(change, index) in item.changes" :key="`${item.id}-${index}`">
                                <template v-if="change.work_date">
                                    {{ change.employee_name || `Строка #${change.line_id}` }},
                                    {{ formatDate(change.work_date) }}:
                                    <strong>{{ entryValue(change.before) }}</strong>
                                    <i class="fas fa-long-arrow-alt-right" aria-hidden="true"></i>
                                    <strong>{{ entryValue(change.after) }}</strong>
                                </template>
                                <template v-else>
                                    {{ change.employee_name || `Строка #${change.line_id}` }}: изменены КТУ/характеристики
                                </template>
                            </li>
                        </ul>
                    </template>
                </article>
            </li>
        </ol>
    </section>
</template>

<script>
import {discussionFeedMixin} from "@/utils/discussions";
export default {
    mixins: [discussionFeedMixin],
    name: 'TimesheetActivity',
    props: {
        items: {type: Array, default: () => []},
        loading: {type: Boolean, default: false},
        pending: {type: Boolean, default: false}
    },
    data() {
        return {
            activeSection: 'comments',
            newComment: '',
            editingCommentId: null,
            editCommentText: '',
            openEvents: {}
        };
    },
    computed: {
        commentItems() {
            return this.items.filter(item => item.kind === 'comment');
        },
        historyItems() {
            return this.items.filter(item => item.kind !== 'comment');
        },
        displayedItems() {
            return this.activeSection === 'comments' ? this.commentItems : this.historyItems;
        }
    },
    methods: {
        createComment() {
            const body = this.newComment.trim();
            if (!body) return;
            this.$emit('create-comment', body);
        },
        beginEdit(item) {
            this.editingCommentId = item.comment_id;
            this.editCommentText = item.body || '';
        },
        cancelEdit() {
            this.editingCommentId = null;
            this.editCommentText = '';
        },
        saveEdit(item) {
            if (!this.editCommentText.trim()) return;
            this.$emit('edit-comment', {item, body: this.editCommentText.trim()});
        },
        eventLabel(item) {
            if (String(item.event_type).startsWith('submission_extension_')) return item.payload && item.payload.description || 'Изменение заявки на продление';
            const labels = {
                edit_session: 'Изменил табель',
                day_entries_saved: 'Изменил отметки табеля',
                line_details_saved: 'Изменил КТУ или характеристику сотрудника',
                characteristic_saved: 'Изменил общую характеристику сотрудника за месяц',
                bulk_edit: 'Выполнил массовое заполнение дня',
                hr_correction_edit: 'Внёс исправления как табельщик',
                timesheet_submitted: 'Отправил табель на проверку',
                timesheet_package_decision: item.payload && item.payload.action === 'cancel'
                    ? 'Отменил исключение табеля из комплекта месяца' : 'Исключил табель из комплекта месяца',
                package_decision_report_changed: 'Изменились сменные отчёты: решение об исключении табеля требует повторной проверки',
                timesheet_returned: 'Вернул табель на доработку',
                timesheet_approved: 'Утвердил табель',
                hr_correction_started: 'Открыл самостоятельное исправление табеля',
                timesheet_returned_from_payroll: 'Вернул табель из расчёта зарплаты',
                period_handed_to_payroll: 'Передал комплект периода на расчёт зарплаты',
                period_corrections_handed_to_payroll: 'Передал исправленный комплект на расчёт зарплаты',
                source_report_changed: 'Изменился связанный ежесменный отчёт; табель требует повторной сверки',
                source_report_changed_after_payroll_handoff: 'После передачи на расчёт изменился связанный ежесменный отчёт',
                employee_line_added: 'Добавил сотрудника в табель',
                employee_line_added_manually: 'Добавил сотрудника в табель вручную',
                manual_line_removed: 'Удалил ошибочно добавленную строку из табеля',
                roster_member_excluded: 'Убрал сотрудника из состава именованного табеля',
                timesheet_employee_excluded: 'Убрал сотрудника из табеля',
                timesheet_employee_restored: 'Вернул сотрудника в табель',
                timesheet_named: 'Сохранил существующий табель как основной',
                timesheet_responsible_changed: 'Изменил название или ответственного за табель',
                assigned_roster_synchronized: 'Добавил в табель сотрудников после назначения на объект',
                unassigned_empty_roster_lines_removed: 'Автоматически убраны пустые строки без назначений за месяц',
                unresolved_person_added: 'Добавил человека без карточки сотрудника',
                unresolved_person_line_added: 'Добавил человека без карточки сотрудника',
                unresolved_person_line_merged: 'Сопоставил ручную строку с карточкой сотрудника',
                unresolved_person_line_merge_undone: 'Отменил сопоставление ручной строки с карточкой',
                employee_assigned_from_timesheet: 'Оформил назначение сотрудника на объект',
                employee_assigned_from_incident: 'Оформил назначение сотрудника из табеля',
                absence_suggestions_applied: 'Проставил кадровые отсутствия',
                absence_suggestions_undone: 'Отменил массовое проставление кадровых отсутствий',
                absence_discrepancy_resolved: 'Разобрал изменение кадрового отсутствия'
            };
            const base = labels[item.event_type] || 'Зафиксировано изменение табеля';
            if (['timesheet_employee_excluded', 'timesheet_employee_restored'].includes(item.event_type)) {
                const payload = item.payload || {};
                return `${base}: ${payload.employee_name}`;
            }
            if (['manual_line_removed', 'roster_member_excluded'].includes(item.event_type)) {
                const employeeName = item.payload && item.payload.employee_name;
                return employeeName ? `${base}: ${employeeName}` : base;
            }
            if (item.event_type === 'absence_discrepancy_resolved') {
                const payload = item.payload || {};
                const action = payload.action === 'sync'
                    ? 'синхронизировал отметку'
                    : 'оставил отметку без изменения';
                const subject = payload.employee_name
                    ? `${payload.employee_name}, ${this.formatDate(payload.work_date)}`
                    : this.formatDate(payload.work_date);
                return `${base}: ${action}${subject ? ` — ${subject}` : ''}`;
            }
            return base;
        },
        rosterEmployees(item) {
            return item && item.payload && Array.isArray(item.payload.employees)
                ? item.payload.employees
                : [];
        },
        assignmentLabel(assignment) {
            const kinds = {
                primary_object: 'Основное назначение на объект',
                primary_group: 'Назначение через группу объектов',
                additional: 'Дополнительное назначение на объект'
            };
            const source = assignment && assignment.source === 'planned_move_confirmation'
                ? 'Прибытие по плану подтверждено'
                : (kinds[assignment && assignment.kind] || 'Назначение на объект');
            const effectiveFrom = assignment && (
                assignment.source_effective_from || assignment.starts_on
            );
            return effectiveFrom
                ? `${source} с ${this.formatDate(effectiveFrom)}`
                : source;
        },
        entryValue(value) {
            if (!value) return 'пусто';
            const result = [];
            if (Number(value.worked_minutes) > 0) result.push(`${Number(value.worked_minutes) / 60} ч`);
            if (value.absence_code || value.absence_code_id) {
                result.push(value.absence_code || `код #${value.absence_code_id}`);
            }
            if (value.travel_code || value.travel_code_id) {
                result.push(value.travel_code || `код #${value.travel_code_id}`);
            }
            return result.join(' + ') || 'пусто';
        },
        formatDate(value) {
            if (!value) return '';
            return new Intl.DateTimeFormat('ru-RU').format(new Date(`${value}T00:00:00`));
        },
        formatDateTime(value) {
            if (!value) return '';
            return new Intl.DateTimeFormat('ru-RU', {
                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
            }).format(new Date(value));
        }
    }
};
</script>

<style scoped lang="scss">
.timesheet-activity {
    margin-top: 1.25rem;
    padding: 1rem;
    border: 1px solid #d8e2ee;
    border-radius: .8rem;
    background: #fff;
}
.timesheet-activity__header,
.timesheet-activity__composer,
.timesheet-activity__feed article > header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: .75rem;
}
.timesheet-activity__header h2 { margin: 0; font-size: 1.05rem; }
.timesheet-activity__header p { margin: .2rem 0 0; color: #64748b; font-size: .85rem; }
.timesheet-activity__tabs {
    display: flex;
    gap: .35rem;
    margin-top: 1rem;
    padding: .25rem;
    border-radius: .65rem;
    background: #eef3f8;
}
.timesheet-activity__tabs button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: .45rem;
    min-height: 2.35rem;
    padding: .4rem .85rem;
    border: 0;
    border-radius: .5rem;
    color: #526276;
    background: transparent;
    font-weight: 600;
}
.timesheet-activity__tabs button.active {
    color: #0f5f9c;
    background: #fff;
    box-shadow: 0 1px 4px rgba(30, 58, 86, .14);
}
.timesheet-activity__tabs button span {
    min-width: 1.35rem;
    padding: .08rem .35rem;
    border-radius: 999px;
    background: #d9e4ef;
    font-size: .72rem;
    line-height: 1.2;
}
.timesheet-activity__tabs button.active span { background: #dceeff; }
.timesheet-activity__composer { margin: 1rem 0; align-items: flex-end; }
.timesheet-activity__composer textarea { flex: 1; }
.timesheet-activity__empty { padding: 1rem; color: #64748b; text-align: center; }
.timesheet-activity__feed { list-style: none; margin: 0; padding: 0; }
.timesheet-activity__feed > li { position: relative; display: grid; grid-template-columns: 2rem 1fr; gap: .65rem; padding: .65rem 0; }
.timesheet-activity__feed > li + li { border-top: 1px solid #edf2f7; }
.timesheet-activity__marker { display: grid; place-items: center; width: 2rem; height: 2rem; border-radius: 50%; color: #1769aa; background: #e8f4ff; }
.timesheet-activity__feed article { min-width: 0; }
.timesheet-activity__feed article > header time,
.timesheet-activity__feed article small { color: #758397; font-size: .75rem; }
.timesheet-activity__feed p { margin: .35rem 0; white-space: pre-wrap; }
.timesheet-activity__deleted { color: #758397; font-style: italic; }
.timesheet-activity__comment-actions { display: flex; gap: .5rem; align-items: center; }
.timesheet-activity__comment-actions button:not(.btn) { padding: 0; border: 0; color: #1769aa; background: transparent; font-size: .78rem; }
.timesheet-activity__comment-actions button.is-danger { color: #b42318; }
.timesheet-activity__details-toggle { padding: 0; border: 0; color: #1769aa; background: transparent; font-size: .82rem; }
.timesheet-activity__changes { margin: .5rem 0 0; padding-left: 1.25rem; color: #475569; font-size: .82rem; }
.timesheet-activity__roster-additions {
    display: grid;
    gap: .4rem;
    margin: .45rem 0 0;
    padding: 0;
    list-style: none;
}
.timesheet-activity__roster-additions li {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: .08rem;
    padding: .45rem .6rem;
    border-left: 3px solid #64a7d3;
    border-radius: .25rem;
    background: #f4f9fd;
}
.timesheet-activity__roster-additions span { color: #64748b; font-size: .8rem; }
@media (max-width: 767px) {
    .timesheet-activity { padding: .8rem; }
    .timesheet-activity__header { align-items: flex-start; }
    .timesheet-activity__header p { display: none; }
    .timesheet-activity__tabs button { flex: 1; padding-inline: .5rem; }
    .timesheet-activity__composer { align-items: stretch; flex-direction: column; }
    .timesheet-activity__feed article > header { align-items: flex-start; flex-direction: column; gap: 0; }
}
</style>
